import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import api from '../../utils/api';
import AdminHeader from '../../components/AdminHeader';
import './AdminProducts.css';

const formatPrice = (value) => {
  if (value == null || value === '') {
    return '-';
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return '-';
  }
  return `฿${numericValue.toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

const parseNumeric = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const hasPromotionPrices = (product = {}) => {
  // Check old promo prices
  const cash = parseNumeric(product.price_cash);
  const cashPromo = parseNumeric(product.price_cash_promo);
  if (cashPromo != null && (cash == null || cashPromo < cash)) {
    return true;
  }
  const installment = parseNumeric(product.price_installment);
  const installmentPromo = parseNumeric(product.price_installment_promo);
  if (installmentPromo != null && (installment == null || installmentPromo < installment)) {
    return true;
  }
  // Check new promotion system
  if (product.promo_enabled) {
    const now = new Date();
    const startAt = product.promo_start_at ? new Date(product.promo_start_at) : null;
    const endAt = product.promo_end_at ? new Date(product.promo_end_at) : null;
    if (startAt && endAt && now >= startAt && now <= endAt) {
      return true;
    }
  }
  return false;
};

// Calculate current selling price based on promotion
const calculateSellingPrice = (basePrice, promoEnabled, promoType, promoValue, promoStartAt, promoEndAt) => {
  // If no base price, return null
  if (!basePrice) {
    return null;
  }
  
  // If promotion not enabled, return base price
  if (!promoEnabled) {
    return parseNumeric(basePrice);
  }
  
  const now = new Date();
  const startAt = promoStartAt ? new Date(promoStartAt) : null;
  const endAt = promoEndAt ? new Date(promoEndAt) : null;
  
  // Check if promotion is active
  if (!startAt || !endAt || now < startAt || now > endAt) {
    return parseNumeric(basePrice);
  }
  
  const base = parseNumeric(basePrice);
  if (!base) return parseNumeric(basePrice);
  
  const value = parseNumeric(promoValue);
  if (!value) return parseNumeric(basePrice);
  
  let sellPrice = base;
  
  switch (promoType) {
    case 'flat':
      sellPrice = base - value;
      break;
    case 'percent':
      sellPrice = base * (1 - value / 100);
      break;
    case 'special_price':
      sellPrice = value;
      break;
    default:
      return parseNumeric(basePrice);
  }
  
  // Ensure price is not negative
  return Math.max(0, Math.round(sellPrice));
};

const DISPLAY_CATEGORY_OPTIONS = [
  'เตียงนอน',
  'โซฟา',
  'ชั้นวางทีวี',
  'โต๊ะเครื่องแป้ง',
  'ตู้เสื้อผ้า',
  'ฟูกนอน/ที่นอน',
  'ตู้โชว์',
  'หิ้งพระ',
  'ตู้กับข้าว',
  'ชุดเซ็ต'
];

const PROMOTION_FILTER_OPTIONS = [
  { label: 'ทั้งหมด', value: 'all' },
  { label: 'มีโปรฯ', value: 'promo' },
  { label: 'ไม่มีโปรฯ', value: 'regular' }
];

const MAX_PRODUCT_IMAGES = 4;

const normalizeImageArray = (value) => {
  const source = Array.isArray(value) ? value : value ? [value] : [];
  const cleaned = source
    .map((item) => (item == null ? '' : String(item).trim()))
    .filter(Boolean);
  const padded = cleaned.slice(0, MAX_PRODUCT_IMAGES);
  while (padded.length < MAX_PRODUCT_IMAGES) {
    padded.push('');
  }
  return padded;
};

const compactImages = (images = []) =>
  images
    .map((item) => (item == null ? '' : String(item).trim()))
    .filter(Boolean)
    .slice(0, MAX_PRODUCT_IMAGES);

const resolveImagePreview = (value) => {
  if (!value) {
    return null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `/images/${trimmed}`;
};

// Memoized Price Preview Component
const PricePreview = React.memo(({ formData }) => {
  const pricePreview = useMemo(() => {
    const now = new Date();
    const startAt = formData.promoStartAt 
      ? new Date(formData.promoStartAt.includes('T') ? formData.promoStartAt : `${formData.promoStartAt}T00:00:00`)
      : null;
    const endAt = formData.promoEndAt 
      ? new Date(formData.promoEndAt.includes('T') ? formData.promoEndAt : `${formData.promoEndAt}T00:00:00`)
      : null;
    const isActive = startAt && endAt && now >= startAt && now <= endAt;
    
    const cashBase = parseNumeric(formData.priceCash);
    const installmentBase = parseNumeric(formData.priceInstallment);
    
    let cashSell = cashBase;
    let installmentSell = installmentBase;
    
    if (isActive && formData.promoValue) {
      if (formData.promoTarget === 'cash' || formData.promoTarget === 'both') {
        cashSell = calculateSellingPrice(
          cashBase,
          true,
          formData.promoType,
          formData.promoValue,
          formData.promoStartAt,
          formData.promoEndAt
        );
      }
      if (formData.promoTarget === 'installment' || formData.promoTarget === 'both') {
        installmentSell = calculateSellingPrice(
          installmentBase,
          true,
          formData.promoType,
          formData.promoValue,
          formData.promoStartAt,
          formData.promoEndAt
        );
      }
    }
    
    return { cashBase, installmentBase, cashSell, installmentSell, isActive };
  }, [
    formData.promoStartAt,
    formData.promoEndAt,
    formData.priceCash,
    formData.priceInstallment,
    formData.promoValue,
    formData.promoTarget,
    formData.promoType
  ]);

  const { cashBase, installmentBase, cashSell, installmentSell, isActive } = pricePreview;

  return (
    <div className="admin-products-promo-preview">
      <div className="admin-products-promo-preview__title">ราคาขายปัจจุบัน:</div>
      <div className="admin-products-promo-preview__prices">
        {cashBase && (
          <div>
            <span className="label">สด:</span>
            <strong className={isActive && cashSell !== cashBase ? 'has-promo' : ''}>
              {formatPrice(cashSell)}
            </strong>
            {isActive && cashSell !== cashBase && (
              <span className="original">จาก {formatPrice(cashBase)}</span>
            )}
          </div>
        )}
        {installmentBase && (
          <div>
            <span className="label">ผ่อน:</span>
            <strong className={isActive && installmentSell !== installmentBase ? 'has-promo' : ''}>
              {formatPrice(installmentSell)}
            </strong>
            {isActive && installmentSell !== installmentBase && (
              <span className="original">จาก {formatPrice(installmentBase)}</span>
            )}
          </div>
        )}
      </div>
      <div className="admin-products-promo-preview__status">
        {isActive ? (
          <span className="status-active">🔥 กำลังมีโปร</span>
        ) : (
          <span className="status-inactive">⏰ ยังไม่อยู่ในช่วงโปร</span>
        )}
      </div>
    </div>
  );
});

export default function AdminProducts({ admin, logout, isSidebarCollapsed, toggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categoryMap, setCategoryMap] = useState(new Map());
  const [activeCategory, setActiveCategory] = useState('all');
  const [promotionFilter, setPromotionFilter] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    selectedCategories: []
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priceCash: '',
    priceCashPromo: '',
    priceInstallment: '',
    priceInstallmentPromo: '',
    highlights: '',
    displayCategory: DISPLAY_CATEGORY_OPTIONS[0] || '',
    images: Array(MAX_PRODUCT_IMAGES).fill(''),
    // Promotion fields
    promoEnabled: false,
    promoType: 'flat', // 'flat', 'percent', 'special_price'
    promoValue: '',
    promoTarget: 'both', // 'cash', 'installment', 'both'
    promoStartAt: '',
    promoEndAt: ''
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState({ show: false, text: '', variant: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getCategoryLabel = useCallback((product) => {
    if (!product) {
      return '-';
    }

    if (Array.isArray(product.tags) && product.tags.length > 0) {
      const categoryTag = product.tags
        .map((tag) => (tag == null ? '' : String(tag).trim()))
        .find((tag) => DISPLAY_CATEGORY_OPTIONS.includes(tag));
      if (categoryTag) {
        return categoryTag;
      }
    }

    if (product.category_name && String(product.category_name).trim()) {
      const label = String(product.category_name).trim();
      if (DISPLAY_CATEGORY_OPTIONS.includes(label)) {
        return label;
      }
    }

    if (product.category_id != null) {
      const categoryIdString = String(product.category_id);
      for (const [label, id] of categoryMap.entries()) {
        if (String(id) === categoryIdString) {
          return label;
        }
      }
    }

    return '-';
  }, [categoryMap]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);


  const handleAdd = useCallback(() => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      priceCash: '',
      priceCashPromo: '',
      priceInstallment: '',
      priceInstallmentPromo: '',
      highlights: '',
      displayCategory: DISPLAY_CATEGORY_OPTIONS[0] || '',
      images: Array(MAX_PRODUCT_IMAGES).fill(''),
      promoEnabled: false,
      promoType: 'flat',
      promoValue: '',
      promoTarget: 'both',
      promoStartAt: '',
      promoEndAt: ''
    });
    setImageFiles([]);
    setImagePreview(null);
    setIsDrawerOpen(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    if (action === 'add') {
      handleAdd();
      params.delete('action');
      const nextSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate, handleAdd]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      const data = Array.isArray(response.data) ? response.data : [];
      const map = new Map(
        data.map((item) => [(item.category_name || '').trim(), item.category_id])
      );
      setCategoryMap(map);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategoryMap(new Map());
    }
  };

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/products');
      const data = Array.isArray(response.data) ? response.data : [];
      const normalized = data.map((item) => {
        const images = Array.isArray(item.product_images)
          ? item.product_images
              .map((img) => (img == null ? '' : String(img).trim()))
              .filter(Boolean)
          : item.product_image
          ? [String(item.product_image).trim()]
          : [];
        const heroImage = images[0] || (item.product_image ? String(item.product_image).trim() : '');
        return {
          ...item,
          category_id: item.category_id != null ? String(item.category_id) : '',
          category_name: (item.category_name || '').trim(),
          tags: Array.isArray(item.tags)
            ? item.tags
                .map((tag) => (tag == null ? '' : String(tag).trim()))
                .filter(Boolean)
            : [],
          product_image: heroImage,
          product_images: images,
          highlights: Array.isArray(item.highlights) ? item.highlights : [],
          // Ensure promotion fields exist
          promo_enabled: item.promo_enabled || false,
          promo_type: item.promo_type || null,
          promo_value: item.promo_value || null,
          promo_target: item.promo_target || null,
          promo_start_at: item.promo_start_at || null,
          promo_end_at: item.promo_end_at || null
        };
      });
      setProducts(normalized);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setIsLoading(false);
      setMessage({ show: true, text: 'เกิดข้อผิดพลาดในการโหลดข้อมูลสินค้า', variant: 'danger' });
      setTimeout(() => setMessage({ show: false }), 3000);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);


  const handleImageChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setImageFiles(files);
      
      // Preview ไฟล์แรกที่เลือกทันที
      const firstFile = files[0];
      if (firstFile && firstFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target.result;
          setImagePreview(result);
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          setImagePreview(null);
        };
        reader.readAsDataURL(firstFile);
      } else {
        setImagePreview(null);
      }
      
      // อัปเดต formData โดยให้รูปแรกที่เลือกอยู่ในตำแหน่งแรก และล้างรูปเก่าออก
      setFormData((prev) => {
        const nextImages = Array(MAX_PRODUCT_IMAGES).fill('');
        // รูปแรกที่เลือกอยู่ในตำแหน่งแรก (ใช้ชื่อไฟล์ชั่วคราว)
        nextImages[0] = firstFile.name;
        // รูปที่เหลืออยู่ในตำแหน่งถัดไป
        files.slice(1, 4).forEach((file, index) => {
          if (index + 1 < MAX_PRODUCT_IMAGES) {
            nextImages[index + 1] = file.name;
          }
        });
        return { ...prev, images: nextImages };
      });
    } else {
      setImageFiles([]);
      setImagePreview(null);
    }
  }, []);

  const handleImageFieldChange = useCallback((index, value) => {
    setFormData((prev) => {
      const nextImages = [...prev.images];
      nextImages[index] = value;
      return { ...prev, images: nextImages };
    });
    // Preview any image field that has a value (not just index 0)
    if (value && value.trim()) {
      // If user types URL in any field, clear the uploaded files if it's the first field
      if (index === 0 && imageFiles.length > 0) {
        setImageFiles([]);
      }
      setImagePreview(resolveImagePreview(value));
    } else if (index === 0 && !value) {
      // If first field is cleared, clear preview
      setImagePreview(null);
    }
  }, [imageFiles.length]);

  const handleMainImageChange = useCallback((e) => {
    const value = e.target.value;
    handleImageFieldChange(0, value);
    // Show preview when user types in main image field
    if (value && value.trim()) {
      setImagePreview(resolveImagePreview(value));
    } else {
      setImagePreview(null);
    }
  }, [handleImageFieldChange]);

  const handleMainImageFocus = useCallback((e) => {
    // Show preview when user focuses on main image field with a value
    if (e.target.value && e.target.value.trim()) {
      setImagePreview(resolveImagePreview(e.target.value));
    }
  }, []);

  const createExtraImageChangeHandler = useCallback((index) => {
    return (e) => {
      const value = e.target.value;
      handleImageFieldChange(index, value);
      // Show preview when user types in any image field
      if (value && value.trim()) {
        setImagePreview(resolveImagePreview(value));
      }
    };
  }, [handleImageFieldChange]);

  const handleExtraImageFocus = useCallback((e) => {
    // Show preview when user focuses on a field with a value
    if (e.target.value && e.target.value.trim()) {
      setImagePreview(resolveImagePreview(e.target.value));
    }
  }, []);

  const handlePromoStartDateChange = useCallback((e) => {
    const dateValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      promoStartAt: dateValue ? `${dateValue}T00:00:00` : ''
    }));
  }, []);

  const handlePromoEndDateChange = useCallback((e) => {
    const dateValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      promoEndAt: dateValue ? `${dateValue}T00:00:00` : ''
    }));
  }, []);

  const handlePromoEnabledChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, promoEnabled: e.target.checked }));
  }, []);

  const handleEdit = (product) => {
    setEditingProduct(product);
    const imagesForState = normalizeImageArray(
      Array.isArray(product.product_images) && product.product_images.length > 0
        ? product.product_images
        : product.product_image
        ? [product.product_image]
        : []
    );
    const existingDisplayCategory = Array.isArray(product.tags)
      ? product.tags
          .map((tag) => (tag == null ? '' : String(tag).trim()))
          .find((tag) => DISPLAY_CATEGORY_OPTIONS.includes(tag))
      : '';
    const fallbackCategory = getCategoryLabel(product);
    const resolvedDisplayCategory = DISPLAY_CATEGORY_OPTIONS.includes(existingDisplayCategory)
      ? existingDisplayCategory
      : DISPLAY_CATEGORY_OPTIONS.includes(fallbackCategory)
      ? fallbackCategory
      : DISPLAY_CATEGORY_OPTIONS[0] || '';

    // Format date for input fields (YYYY-MM-DD) - extract only date part
    const formatDateLocal = (dateString) => {
      if (!dateString) return '';
      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      // If it's in datetime format (YYYY-MM-DDTHH:mm:ss), extract date part
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      // Try to parse as Date and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFormData({
      name: product.product_name,
      description: product.product_description || '',
      priceCash: product.price_cash ?? '',
      priceCashPromo: product.price_cash_promo ?? '',
      priceInstallment: product.price_installment ?? '',
      priceInstallmentPromo: product.price_installment_promo ?? '',
      highlights: (product.highlights || []).join('\n'),
      displayCategory: resolvedDisplayCategory,
      images: imagesForState,
      promoEnabled: product.promo_enabled || false,
      promoType: product.promo_type || 'flat',
      promoValue: product.promo_value ?? '',
      promoTarget: product.promo_target || 'both',
      promoStartAt: formatDateLocal(product.promo_start_at),
      promoEndAt: formatDateLocal(product.promo_end_at)
    });
    setImageFiles([]);
    setImagePreview(resolveImagePreview(imagesForState[0]));
    setIsDrawerOpen(true);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: ตรวจสอบว่ามีชื่อสินค้าหรือไม่
    if (!formData.name || !formData.name.trim()) {
      alert('กรุณากรอกชื่อสินค้า');
      return;
    }
    
    // Validation: ตรวจสอบว่ามีราคาอย่างน้อย 1 ราคา
    if (!formData.priceCash && !formData.priceInstallment) {
      alert('กรุณากรอกราคา (ซื้อสด หรือ ซื้อผ่อน) อย่างน้อย 1 ราคา');
      return;
    }
    
    setIsLoading(true);

    try {
      // ถ้ามีไฟล์ที่เลือกใหม่ ให้ใช้ชื่อไฟล์จาก imageFiles
      // ถ้าไม่มี ให้ใช้ URL จาก formData.images
      let imageList = [];
      if (imageFiles.length > 0) {
        // ใช้ชื่อไฟล์จากไฟล์ที่เลือกใหม่ (รูปแรกที่เลือกจะอยู่ในตำแหน่งแรก)
        imageList = imageFiles.slice(0, 4).map(file => file.name);
        // เพิ่ม URL จาก formData.images ถ้ามี (สำหรับรูปที่ไม่ได้เลือกใหม่)
        const existingUrls = compactImages(formData.images).filter(url => 
          url && !imageFiles.some(file => file.name === url)
        );
        imageList = [...imageList, ...existingUrls].slice(0, MAX_PRODUCT_IMAGES);
      } else {
        // ไม่มีไฟล์ใหม่ ใช้ URL จาก formData.images
        imageList = compactImages(formData.images);
      }
      
      const displayCategoryTag = formData.displayCategory
        ? String(formData.displayCategory).trim()
        : '';
      const tagsToSend = displayCategoryTag ? [displayCategoryTag] : [];
      const categoryIdFromLabel = displayCategoryTag
        ? categoryMap.get(displayCategoryTag) ?? null
        : null;

      const productData = {
        name: formData.name,
        description: formData.description,
        categoryId: categoryIdFromLabel,
        tags: tagsToSend,
        highlights: formData.highlights
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
        priceCash: formData.priceCash !== '' ? parseFloat(formData.priceCash) : null,
        priceCashPromo: formData.priceCashPromo !== '' ? parseFloat(formData.priceCashPromo) : null,
        priceInstallment: formData.priceInstallment !== '' ? parseFloat(formData.priceInstallment) : null,
        priceInstallmentPromo:
          formData.priceInstallmentPromo !== '' ? parseFloat(formData.priceInstallmentPromo) : null,
        image: imageList[0] || '',
        images: imageList,
        // Promotion data
        promoEnabled: formData.promoEnabled || false,
        promoType: formData.promoEnabled ? formData.promoType : null,
        promoValue: formData.promoEnabled && formData.promoValue !== '' ? parseFloat(formData.promoValue) : null,
        promoTarget: formData.promoEnabled ? formData.promoTarget : null,
        promoStartAt: formData.promoEnabled && formData.promoStartAt 
          ? (formData.promoStartAt.includes('T') 
              ? new Date(formData.promoStartAt).toISOString() 
              : new Date(`${formData.promoStartAt}T00:00:00`).toISOString())
          : null,
        promoEndAt: formData.promoEnabled && formData.promoEndAt 
          ? (formData.promoEndAt.includes('T') 
              ? new Date(formData.promoEndAt).toISOString() 
              : new Date(`${formData.promoEndAt}T00:00:00`).toISOString())
          : null
      };

      // If there's an uploaded file, use FormData, otherwise use JSON
      if (imageFiles.length > 0) {
        const formDataToSend = new FormData();
        // ส่งไฟล์ทั้งหมด (สูงสุด 4 ไฟล์)
        imageFiles.slice(0, 4).forEach((file) => {
          formDataToSend.append('imageFiles', file);
        });
        formDataToSend.append('data', JSON.stringify(productData));

        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        };

        if (editingProduct) {
          await api.put(`/api/products/${editingProduct.product_id}`, formDataToSend, config);
        } else {
          await api.post('/api/products', formDataToSend, config);
        }
      } else {
        // No file upload, use regular JSON
        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        };
        if (editingProduct) {
          await api.put(`/api/products/${editingProduct.product_id}`, productData, config);
        } else {
          await api.post('/api/products', productData, config);
        }
      }
      fetchProducts();
      setIsDrawerOpen(false);
      setImageFiles([]);
      setImagePreview(null);
      setMessage({ show: true, text: editingProduct ? 'แก้ไขสินค้าสำเร็จ ✅' : 'เพิ่มสินค้าสำเร็จ ✅', variant: 'success' });
      setTimeout(() => setMessage({ show: false }), 3000);
    } catch (error) {
      console.error('=== Error saving product ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
      
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.error || error.response.data?.message || errorMessage;
        console.error('Server error:', error.response.status, errorMessage);
      } else if (error.request) {
        // Request was made but no response
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        console.error('No response from server');
      } else {
        // Something else happened
        errorMessage = error.message || errorMessage;
        console.error('Request setup error');
      }
      
      // แสดง error เป็น alert
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('ยืนยันการลบสินค้านี้?')) {
      return;
    }

    try {
      await api.delete(`/api/products/${productId}`);
      setMessage({ show: true, text: 'ลบสินค้าสำเร็จ ✅', variant: 'success' });
      fetchProducts();
      setTimeout(() => setMessage({ show: false }), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage({ show: true, text: 'เกิดข้อผิดพลาด', variant: 'danger' });
    }
  };

  const handleViewDetails = (product) => {
    // Navigate to product detail page or open in modal/drawer
    // For now, just open edit drawer
    handleEdit(product);
  };

  const handleClearFilters = () => {
    setAdvancedFilters({
      selectedCategories: []
    });
    setProductSearch('');
    setActiveCategory('all');
    setPromotionFilter('all');
    setSortBy('newest');
  };

  const columns = [
    {
      key: 'product_id',
      header: 'ID',
      className: 'text-center product-id-col',
      render: (product) => product.product_id
    },
    {
      key: 'product_name',
      header: 'สินค้า',
      className: 'product-name-col',
      render: (product) => {
        const showPromotion = hasPromotionPrices(product);
        return (
          <div className="product-name-cell">
            <div className="product-name-cell__main">
              <span className="product-name-cell__name">{product.product_name}</span>
              {showPromotion && <span className="product-promo-badge">มีโปรฯ</span>}
            </div>
            <div className="product-name-cell__meta">
              <span className="product-name-cell__category">{getCategoryLabel(product)}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'category',
      header: 'หมวดหมู่',
      className: 'category-col text-muted',
      render: (product) => getCategoryLabel(product)
    },
    {
      key: 'price_summary',
      header: 'ราคาขาย(สด/ผ่อน)',
      className: 'price-summary-col',
      render: (product) => {
        // Get base prices
        const cash = parseNumeric(product.price_cash);
        const installment = parseNumeric(product.price_installment);
        
        // Calculate selling prices using new promotion system
        let currentCashPrice = cash;
        let currentInstallmentPrice = installment;
        
        // Check if new promotion system is active
        if (product.promo_enabled && product.promo_start_at && product.promo_end_at) {
          const now = new Date();
          const startAt = new Date(product.promo_start_at);
          const endAt = new Date(product.promo_end_at);
          
          if (!isNaN(startAt.getTime()) && !isNaN(endAt.getTime()) && now >= startAt && now <= endAt) {
            const promoTarget = product.promo_target || product.promoTarget || 'both';
            // Calculate cash price
            if ((promoTarget === 'cash' || promoTarget === 'both') && cash) {
              const calculated = calculateSellingPrice(
                cash,
                true,
                product.promo_type,
                product.promo_value,
                product.promo_start_at,
                product.promo_end_at
              );
              if (calculated !== null) {
                currentCashPrice = calculated;
              }
            }
            // Calculate installment price
            if ((promoTarget === 'installment' || promoTarget === 'both') && installment) {
              const calculated = calculateSellingPrice(
                installment,
                true,
                product.promo_type,
                product.promo_value,
                product.promo_start_at,
                product.promo_end_at
              );
              if (calculated !== null) {
                currentInstallmentPrice = calculated;
              }
            }
          }
        }
        
        // Fallback to old promo prices if new system not active or not available
        if (!product.promo_enabled || !product.promo_start_at || !product.promo_end_at) {
          const cashPromo = parseNumeric(product.price_cash_promo);
          const installmentPromo = parseNumeric(product.price_installment_promo);
          if (cashPromo !== null) currentCashPrice = cashPromo;
          if (installmentPromo !== null) currentInstallmentPrice = installmentPromo;
        }
        
        // Ensure we have at least one price
        if ((currentCashPrice === null || currentCashPrice === undefined) && 
            (currentInstallmentPrice === null || currentInstallmentPrice === undefined)) {
          return <span className="text-muted">ยังไม่ระบุ</span>;
        }

        // Format price without currency symbol for compact display
        const formatPriceCompact = (value) => {
          if (value == null || value === '' || value === undefined) {
            return '-';
          }
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) {
            return '-';
          }
          return numericValue.toLocaleString('th-TH', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
        };

        const cashDisplay = (currentCashPrice !== null && currentCashPrice !== undefined) 
          ? formatPriceCompact(currentCashPrice) 
          : '-';
        const installmentDisplay = (currentInstallmentPrice !== null && currentInstallmentPrice !== undefined)
          ? formatPriceCompact(currentInstallmentPrice)
          : '-';

        return (
          <div className="product-price-cell">
            <div className="product-price-cell__compact">
              <span className="product-price-cell__compact-price">
                {cashDisplay}/{installmentDisplay}
              </span>
                    </div>
          </div>
        );
      }
    },
    {
      key: 'highlights',
      header: 'รายละเอียด',
      className: 'highlights-col',
      render: (product) =>
        product.highlights && product.highlights.length > 0 ? (
          <div className="product-highlights">
            {product.highlights.slice(0, 2).map((highlight, idx) => (
              <div key={idx} className="product-highlight-item">
                {highlight}
              </div>
            ))}
            {product.highlights.length > 2 && (
              <div className="product-highlight-item text-muted">...</div>
            )}
          </div>
        ) : (
          <span className="text-muted">-</span>
        )
    },
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-nowrap text-end actions-col',
      render: (product) => (
        <div className="admin-product-actions">
          <button
            className="admin-product-action-btn admin-product-action-btn--view"
            onClick={() => handleViewDetails(product)}
            title="ดูรายละเอียด"
          >
            <i className="bi bi-eye"></i>
          </button>
          <button
            className="admin-product-action-btn admin-product-action-btn--edit"
            onClick={() => handleEdit(product)}
            title="แก้ไข"
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            className="admin-product-action-btn admin-product-action-btn--delete"
            onClick={() => handleDelete(product.product_id)}
            title="ลบ"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      )
    }
  ];

  const categoryFilters = useMemo(() => {
    return [
      { label: 'ทั้งหมด', value: 'all' },
      ...DISPLAY_CATEGORY_OPTIONS.map((label) => ({
        label,
        value: label
      }))
    ];
  }, []);

  useEffect(() => {
    const allowedValues = new Set(['all', ...DISPLAY_CATEGORY_OPTIONS]);
    if (!allowedValues.has(activeCategory)) {
      setActiveCategory('all');
    }
  }, [activeCategory]);

  const categoryProducts = useMemo(() => {
    if (activeCategory === 'all') {
      return products;
    }
    return products.filter((product) => getCategoryLabel(product) === activeCategory);
  }, [products, activeCategory, getCategoryLabel]);

  const searchedProducts = useMemo(() => {
    const trimmed = productSearch.replace(/#/g, '').trim();
    if (!trimmed) {
      return categoryProducts;
    }
    const keyword = trimmed.toLowerCase();
    return categoryProducts.filter((product) => {
      const idMatch = String(product.product_id).toLowerCase().includes(keyword);
      const nameMatch = (product.product_name || '').toLowerCase().includes(keyword);
      return idMatch || nameMatch;
    });
  }, [categoryProducts, productSearch]);

  const filteredProducts = useMemo(() => {
    let result = searchedProducts;
    
    // Apply advanced filters
    if (advancedFilters.selectedCategories.length > 0) {
      result = result.filter((product) => {
        const category = getCategoryLabel(product);
        return advancedFilters.selectedCategories.includes(category);
      });
    }
    
    // Apply promotion filter
    if (promotionFilter !== 'all') {
      const shouldHavePromo = promotionFilter === 'promo';
      result = result.filter((product) => {
        const hasPromo = hasPromotionPrices(product);
        return shouldHavePromo ? hasPromo : !hasPromo;
      });
    }
    
    // Apply sorting
    return [...result].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.product_id - a.product_id;
        case 'oldest':
          return a.product_id - b.product_id;
        case 'price_asc':
          const priceA = parseNumeric(a.price_cash) || parseNumeric(a.price_installment) || 0;
          const priceB = parseNumeric(b.price_cash) || parseNumeric(b.price_installment) || 0;
          return priceA - priceB;
        case 'price_desc':
          const priceA2 = parseNumeric(a.price_cash) || parseNumeric(a.price_installment) || 0;
          const priceB2 = parseNumeric(b.price_cash) || parseNumeric(b.price_installment) || 0;
          return priceB2 - priceA2;
        case 'name_asc':
          return (a.product_name || '').localeCompare(b.product_name || '', 'th');
        case 'name_desc':
          return (b.product_name || '').localeCompare(a.product_name || '', 'th');
        default:
          return 0;
      }
    });
  }, [searchedProducts, promotionFilter, sortBy, advancedFilters, getCategoryLabel]);

  const filteredCount = filteredProducts.length;

  return (
    <div className="admin-products-page">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />
      <Container className="py-5">
        <div className="admin-products-topbar mb-4">
          <div className="admin-products-topbar__title">
            <h2 className="mb-1">จัดการสินค้า</h2>
            <p className="text-muted small mb-0">
              ดูและจัดระเบียบสินค้าพร้อมค้นหาด้วยชื่อหรือรหัสได้ทันที
            </p>
          </div>
          <Link to="/admin/dashboard">
            <Button variant="outline-secondary" className="admin-products-back">
              <i className="bi bi-arrow-left me-2"></i>
              ย้อนกลับ
            </Button>
          </Link>
        </div>

        {message.show && (
          <Alert variant={message.variant} dismissible onClose={() => setMessage({ show: false })}>
            {message.text}
          </Alert>
        )}

        {/* Toolbar */}
        <div className="admin-products-toolbar">
          <div className="admin-products-toolbar__search">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
              placeholder="ค้นหาด้วยชื่อสินค้า, หมวดหมู่ หรือรหัสสินค้า"
                    value={productSearch}
                    onChange={(event) => setProductSearch(event.target.value)}
              onKeyDown={(e) => {
                if (e.key === '/' && e.target !== document.activeElement) {
                  e.preventDefault();
                  e.target.focus();
                }
              }}
                  />
                  {productSearch && (
                    <button
                      type="button"
                className="admin-products-toolbar__search-clear"
                      onClick={() => setProductSearch('')}
                      aria-label="ล้างการค้นหา"
                    >
                      <i className="bi bi-x-circle"></i>
                    </button>
                  )}
                </div>
          <div className="admin-products-toolbar__filters">
            <Button
              variant="outline-secondary"
              className="admin-products-toolbar__advanced-filter-btn"
              onClick={() => setIsAdvancedFilterOpen(true)}
            >
              <i className="bi bi-sliders me-2"></i>
              ตัวกรองขั้นสูง
              {advancedFilters.selectedCategories.length > 0 && (
                <span className="admin-products-toolbar__filter-badge">
                  {advancedFilters.selectedCategories.length}
                </span>
              )}
            </Button>
            <div className="admin-products-toolbar__filter">
              <i className="bi bi-funnel"></i>
              <Form.Select
                value={promotionFilter}
                onChange={(event) => setPromotionFilter(event.target.value)}
              >
                {PROMOTION_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="admin-products-toolbar__filter">
              <i className="bi bi-sort-down"></i>
              <Form.Select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
              >
                <option value="newest">ใหม่ล่าสุด</option>
                <option value="oldest">เก่าที่สุด</option>
                <option value="price_asc">ราคา: ต่ำ → สูง</option>
                <option value="price_desc">ราคา: สูง → ต่ำ</option>
                <option value="name_asc">ชื่อ: ก → ฮ</option>
                <option value="name_desc">ชื่อ: ฮ → ก</option>
              </Form.Select>
            </div>
          </div>
          <Button variant="primary" onClick={handleAdd} className="admin-products-toolbar__add">
            <i className="bi bi-plus-lg me-2"></i>
                  เพิ่มสินค้า
                </Button>
              </div>

        {/* Product Summary */}
        <div className="admin-products-summary">
          <span className="admin-products-summary__label">สินค้าทั้งหมด</span>
          <strong className="admin-products-summary__count">ทั้งหมด {filteredCount} รายการ</strong>
            </div>

        <Card className="admin-products-card">
          <Card.Body>

            <div className="mb-4">
              <div className="category-filter-label mb-2">หมวดหมู่</div>
              <div className="admin-category-filter">
                {categoryFilters.map((category) => (
                  <button
                    key={category.value}
                    type="button"
                    className={`category-chip ${activeCategory === category.value ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category.value)}
                    aria-label={`กรองหมวดหมู่ ${category.label}`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="admin-products-empty">
                <div className="admin-products-empty__icon">
                  <i className="bi bi-inbox"></i>
                </div>
                <h4>ยังไม่มีสินค้าในระบบ</h4>
                <p className="text-muted">เริ่มต้นด้วยการเพิ่มสินค้าใหม่</p>
                <Button variant="primary" onClick={handleAdd}>
                  <i className="bi bi-plus-lg me-2"></i>
                  เพิ่มสินค้าใหม่
                </Button>
              </div>
            ) : (
            <Table responsive hover className="admin-products-table align-middle">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={column.header === 'จัดการ' ? 'text-end' : ''}>
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.product_id}>
                    {columns.map((column) => (
                      <td key={`${product.product_id}-${column.key}`} className={column.className}>
                        {column.render(product)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            )}
          </Card.Body>
        </Card>

        {/* Drawer for Add/Edit Product */}
        {isDrawerOpen && (
          <div className={`admin-products-drawer admin-products-drawer--open`}>
            <div className="admin-products-drawer__overlay" onClick={() => setIsDrawerOpen(false)}></div>
            <div className="admin-products-drawer__content">
            <div className="admin-products-drawer__header">
              <h3>{editingProduct ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}</h3>
              <button
                className="admin-products-drawer__close"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="ปิด"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="admin-products-drawer__body">
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>ชื่อสินค้า</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>คำอธิบาย</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>หมวดหมู่</Form.Label>
                <Form.Select
                  name="displayCategory"
                  value={formData.displayCategory}
                  onChange={handleInputChange}
                >
                  {DISPLAY_CATEGORY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>รายละเอียดสินค้า (หนึ่งรายการต่อบรรทัด)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="highlights"
                  value={formData.highlights}
                  onChange={handleInputChange}
                    placeholder="เช่น&#10;- หนังแท้เกรดพรีเมียม&#10;- โครงสร้างรับประกัน 5 ปี"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ราคา (ซื้อสด)</Form.Label>
                    <Form.Control
                      type="number"
                      name="priceCash"
                      value={formData.priceCash}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ราคา (ซื้อผ่อน)</Form.Label>
                    <Form.Control
                      type="number"
                      name="priceInstallment"
                      value={formData.priceInstallment}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>รูปภาพ</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  multiple
                  onChange={handleImageChange}
                />
                <Form.Text className="text-muted">
                  สามารถเลือกได้สูงสุด 4 รูป (รูปแรกจะเป็นรูปหลัก)
                </Form.Text>
                {imagePreview && (
                  <div className="mt-2">
                    <div className="mb-1 text-muted small">Preview:</div>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '4px',
                        backgroundColor: '#f8f9fa'
                      }}
                      onError={(e) => {
                        console.error('Preview image error:', e);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>ไฟล์หรือ URL รูปภาพหลัก</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.images?.[0] || ''}
                  onChange={handleMainImageChange}
                  onFocus={handleMainImageFocus}
                  placeholder="เช่น product1.png หรือ https://example.com/product1.jpg"
                />
              </Form.Group>

              <Row>
                {[1, 2, 3].map((idx) => (
                  <Col md={4} key={`extra-image-${idx}`}>
                    <Form.Group className="mb-3">
                      <Form.Label>รูปภาพเพิ่มเติม {idx}</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.images?.[idx] || ''}
                        onChange={createExtraImageChangeHandler(idx)}
                        onFocus={handleExtraImageFocus}
                        placeholder="เช่น product1-alt.png"
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>

                {/* Promotion Section */}
                <div className="admin-products-promo-section">
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="promoEnabled"
                      label="มีโปรโมชัน"
                      checked={formData.promoEnabled}
                      onChange={handlePromoEnabledChange}
                      className="admin-products-promo-toggle"
                    />
                  </Form.Group>

                  {formData.promoEnabled && (
                    <div className="admin-products-promo-fields">
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>ชนิดโปรโมชัน</Form.Label>
                            <Form.Select
                              name="promoType"
                              value={formData.promoType}
                              onChange={handleInputChange}
                            >
                              <option value="flat">ลดเป็นจำนวนเงิน (฿)</option>
                              <option value="percent">ลดเป็นเปอร์เซ็นต์ (%)</option>
                              <option value="special_price">ตั้งราคาพิเศษ</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {formData.promoType === 'percent' ? 'เปอร์เซ็นต์ที่ลด (%)' : 
                               formData.promoType === 'special_price' ? 'ราคาพิเศษ (฿)' : 
                               'จำนวนเงินที่ลด (฿)'}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="promoValue"
                              value={formData.promoValue}
                              onChange={handleInputChange}
                              min="0"
                              step={formData.promoType === 'percent' ? '0.01' : '1'}
                              placeholder={formData.promoType === 'percent' ? 'เช่น 10' : 'เช่น 500'}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>โปรโมชันสำหรับ</Form.Label>
                        <Form.Select
                          name="promoTarget"
                          value={formData.promoTarget}
                          onChange={handleInputChange}
                        >
                          <option value="cash">เฉพาะราคาสด</option>
                          <option value="installment">เฉพาะราคาผ่อน</option>
                          <option value="both">ทั้งสดและผ่อน</option>
                        </Form.Select>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>วันที่เริ่มโปรโมชัน</Form.Label>
                            <Form.Control
                              type="date"
                              name="promoStartAt"
                              value={formData.promoStartAt ? formData.promoStartAt.split('T')[0] : ''}
                              onChange={handlePromoStartDateChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>วันที่สิ้นสุดโปรโมชัน</Form.Label>
                            <Form.Control
                              type="date"
                              name="promoEndAt"
                              value={formData.promoEndAt ? formData.promoEndAt.split('T')[0] : ''}
                              onChange={handlePromoEndDateChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Price Preview */}
                      <PricePreview formData={formData} />
                    </div>
                  )}
                </div>

                <div className="admin-products-drawer__footer">
                <Button
                  variant="secondary"
                    onClick={() => setIsDrawerOpen(false)}
                  className="me-2"
                >
                  ยกเลิก
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
              </div>
            </Form>
            </div>
          </div>
        </div>
        )}

        {/* Advanced Filter Side Panel */}
        {isAdvancedFilterOpen && (
          <>
            <div
              className="admin-products-filter-overlay"
              onClick={() => setIsAdvancedFilterOpen(false)}
            />
            <div className={`admin-products-filter-panel ${isAdvancedFilterOpen ? 'is-open' : ''}`}>
              <div className="admin-products-filter-panel__header">
                <h3>
                  <i className="bi bi-sliders"></i>
                  ตัวกรองขั้นสูง
                </h3>
                <button
                  className="admin-products-filter-panel__close"
                  onClick={() => setIsAdvancedFilterOpen(false)}
                  aria-label="ปิด"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="admin-products-filter-panel__body">
                {/* Multiple Categories */}
                <div className="admin-products-filter-section">
                  <h4>หมวดหมู่ (เลือกหลายรายการ)</h4>
                  <div className="admin-products-filter-checkboxes">
                    {DISPLAY_CATEGORY_OPTIONS.map((category) => {
                      const isSelected = advancedFilters.selectedCategories.includes(category);
                      return (
                        <Form.Check
                          key={category}
                          type="checkbox"
                          id={`category-${category}`}
                          label={category}
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdvancedFilters((prev) => ({
                                ...prev,
                                selectedCategories: [...prev.selectedCategories, category]
                              }));
                            } else {
                              setAdvancedFilters((prev) => ({
                                ...prev,
                                selectedCategories: prev.selectedCategories.filter((c) => c !== category)
                              }));
                            }
                          }}
                          className="admin-products-filter-checkbox"
                        />
                      );
                    })}
                  </div>
                </div>


              </div>
              <div className="admin-products-filter-panel__footer">
                <Button
                  variant="outline-secondary"
                  onClick={handleClearFilters}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  ล้างทั้งหมด
                </Button>
                <Button variant="primary" onClick={() => setIsAdvancedFilterOpen(false)}>
                  <i className="bi bi-check-lg me-2"></i>
                  ปรับใช้
                </Button>
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  );
}


